// src/components/TalentProfileModal.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import '../css/TalentProfileModal.css';
import { ProfileAPI } from '../data/profile';
import { addToShortlist, getShortlist } from '../data/shortlist';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from './CreditsContext';

/* =========================
   Per-test lists (core)
========================= */
const BEHAVIORAL_TESTS = [
  { key: 'ocean',  label: 'Big Five Personality (OCEAN Model)' },
  { key: 'disc',   label: 'DISC Assessment' },
  { key: 'sjt',    label: 'Situational Judgement Test (SJT)' },
  { key: 'role',   label: 'Skills & Aptitude Tests (numerical, logical, verbal, role-specific)' },
];

const SKILLS_TESTS = [
  { key: 'cognitive', label: 'Cognitive Ability Test' },
  { key: 'logical',   label: 'Logical Reasoning Test' },
  { key: 'numerical', label: 'Numerical Reasoning Test' },
  { key: 'verbal',    label: 'Verbal Reasoning Test' },
  { key: 'typing',    label: 'Typing Test (Speed & Accuracy)' },
];

/* STAR Method tests for Initial Interview */
const STAR_TESTS = [
  { key: 'situation', label: 'Situation' },
  { key: 'task',      label: 'Task' },
  { key: 'action',    label: 'Action' },
  { key: 'result',    label: 'Result' },
];

/* Second interview (CBI) */
const SECOND_INTERVIEW = [ { key: 'cbi', label: 'Competency Based Interview (CBI)' } ];

/* =========================
   Helpers
========================= */
const splitList = (val) =>
  String(val || '')
    .split(/[,;\n\r]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

/* =========================
   Default (fallback) VM
========================= */
function buildProfileFromTalent(t) {
  const baseSummary =
    'Senior 2D Artist (Generalist) with 6+ years of professional experience in the Philippine game development and outsourcing industry. ' +
    'Specialized in concept art, character and environment asset creation, and production pipeline alignment for mobile and PC game projects. ' +
    'Strong track record in delivering polished visuals for international outsourcing clients, indie studios, and local game startups. ' +
    'Skilled in managing art direction, mentoring mid- and entry-level artists, and ensuring that all deliverables meet global quality standards.';

  const alias = t?.alias || t?.name || 'Unknown';
  const role = t?.role || 'Candidate';
  const candidateId = `${String(t?.id || t?.talentId || 0).padStart(6, '0')}`;

  return {
    name: alias,
    alias: `${alias} • ${role}`,
    candidateId,
    role,
    stage: t?.stage || '',
    yearsExp: 6,
    location: t?.location || t?.workPreferences?.location || 'Philippines / Remote',
    email: t?.email || 'n/a',
    summary: baseSummary,
    snapshot:
      '6+ years in game art production and outsourcing across mobile, web, and PC. ' +
      'Worked with clients in North America, Europe, and Southeast Asia on fantasy, anime-inspired, and casual/midcore titles. ' +
      'Combines strong fundamentals with organized workflows, reducing revision cycles and accelerating delivery.',
    /* NOTE:
       We’ll support BOTH a structured timeline (fallback) and a simple list from DB.
       If DB provides lists, we render them; otherwise we render this timeline. */
    experienceTimeline: [
      {
        title: 'Senior 2D Artist',
        org: 'Exodia Services Inc.',
        location: 'Cebu, Philippines',
        dates: '2021 – Present',
        points: [
          'Produced high-quality concept art and 2D assets aligned to project art direction.',
          'Mentored junior artists; improved onboarding efficiency and asset quality.',
          'Implemented PSD audits and documentation that reduced rework by ~15%.',
        ],
      },
      {
        title: '2D Artist – Freelance',
        org: 'Indie & Outsourcing Projects',
        location: 'Philippines / Remote',
        dates: '2018 – 2021',
        points: [
          'Delivered concept art, UI assets, and environment designs for mobile and web.',
          'Adapted to stylized, anime-inspired, and fantasy art directions.',
          'Integrated assets into Unity pipelines with proper optimization.',
        ],
      },
      {
        title: 'Junior 2D Artist',
        org: 'Local Game Studio (Startup)',
        location: 'Manila, Philippines',
        dates: '2016 – 2018',
        points: [
          'Produced character sprites, props, and basic UI designs.',
          'Supported asset cleanup and iteration cycles under senior guidance.',
          'Learned agile workflows and version control.',
        ],
      },
    ],
    educationTimeline: [
      {
        degree: 'BFA, Major in Visual Communication',
        school: 'University of the Philippines',
        location: 'Philippines',
        dates: '2012 – 2016',
        note: 'Graduated with Honors',
      },
    ],
    skills: {
      technical: [ 'Photoshop', 'Clip Studio Paint', 'Illustrator', 'Spine', 'Live2D', 'Unity (asset integ.)', 'File formats: PSD/PNG/JPG/TIFF/SVG' ],
      creative:  [ 'Concept Art', 'Character & Environment Design', 'Prop Design', 'Color Theory', 'Pipeline Documentation' ],
      collab:    [ 'Git/Perforce', 'Jira/Asana/Trello', 'Notion/Google Workspace', 'Slack/Teams/Zoom' ],
    },
    preferences: [ 'Day shift with overlap for international feedback', 'Full-time; open to project-based adjustments' ],
    certs: [ { name: 'Digital Illustration & Concept Art – CGMA', year: '2020' }, { name: 'Game Art Production Bootcamp – Unity Learn', year: '2019' } ],
    languages: [ 'English — Fluent', 'Filipino — Native', 'Bisaya — Proficient' ],

    // Placeholders for DB-driven lists
    workExpList: null,
    educationList: null,
  };
}

/* =========================
   Component
========================= */
export default function TalentProfileModal({ isOpen, onClose, talent }) {
  const raw = useMemo(() => (talent?.__raw || talent || {}), [talent]);
  const { currentUser } = useAuth();
  const { consumeShortlistPass } = useCredits();

  // Build images list
  const images = useMemo(() => {
    const entry  = raw?.avatars?.Entry  || raw?.avatarEntry  || raw?.avatar1Entry || null;
    const mid    = raw?.avatars?.Mid    || raw?.avatarMid    || raw?.avatar1Mid   || null;
    const senior = raw?.avatars?.Senior || raw?.avatar       || raw?.avatar1Senior|| null;
    return [senior, mid, entry].filter(Boolean);
  }, [raw]);
  const hasImages = images.length > 0;

  const [imgIndex, setImgIndex] = useState(0);
  useEffect(() => { setImgIndex(0); }, [talent]);
  const selectedImage = hasImages ? images[(imgIndex % images.length + images.length) % images.length] : null;
  const nextImg = useCallback(() => setImgIndex(i => (i + 1) % (images.length || 1)), [images.length]);
  const prevImg = useCallback(() => setImgIndex(i => (i - 1 + (images.length || 1)) % (images.length || 1)), [images.length]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Arrow keys navigate images
  useEffect(() => {
    if (!isOpen || !hasImages) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') nextImg();
      if (e.key === 'ArrowLeft')  prevImg();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, hasImages, nextImg, prevImg]);

  // ---------- DB-backed states ----------
  const talentId = useMemo(() => (talent?.talentId ?? talent?.id ?? raw?.talentId ?? raw?.id), [talent, raw]);

  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // Persisted maps/statuses
  const [availMap, setAvailMap] = useState({});
  const [behavioralStatuses, setBehavioralStatuses] = useState({});
  const [skillsStatuses, setSkillsStatuses] = useState({});
  const [starStatuses, setStarStatuses] = useState({});
  const [secondStatuses, setSecondStatuses] = useState({});
  const [clientMsg, setClientMsg] = useState('');

  // NEW: DB profile overlay (your new columns)
  const [dbOverlay, setDbOverlay] = useState(null);

  // Load profile when modal opens
  useEffect(() => {
    if (!isOpen || !talentId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setLoadErr("");
        const p = await ProfileAPI.get(String(talentId));

        // Default statuses = 'Passed' if empty; else use saved
        const def = (list) => Object.fromEntries(list.map(t => [t.key, 'Passed']));
        setBehavioralStatuses(Object.keys(p.behavioralStatuses || {}).length ? p.behavioralStatuses : def(BEHAVIORAL_TESTS));
        setSkillsStatuses(Object.keys(p.skillsStatuses || {}).length ? p.skillsStatuses : def(SKILLS_TESTS));
        setStarStatuses(Object.keys(p.starStatuses || {}).length ? p.starStatuses : def(STAR_TESTS));
        setSecondStatuses(Object.keys(p.secondStatuses || {}).length ? p.secondStatuses : def(SECOND_INTERVIEW));

        setAvailMap(p.availMap || {});
        setClientMsg(p.clientMsg || "");

        // 👉 Overlay from new table columns (tolerant if API doesn’t return some)
        const overlay = {
          profSum: p.prof_sum || '',
          snapExp: p.snap_exp || '',
          workExpList: splitList(p.work_exp),
          educationList: splitList(p.education),
          techSkills: splitList(p.tech_skills),
          creativeSkills: splitList(p.creative_skills),
          collaboration: splitList(p.collaboration),
          workPref: splitList(p.work_pref),
          certTrainings: splitList(p.cert_trainings),
          languages: splitList(p.languages),
        };
        if (!cancelled) setDbOverlay(overlay);
      } catch (e) {
        if (!cancelled) setLoadErr(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, talentId]);

  // Compose profile VM (fallback defaults + DB overlay)
  const profile = useMemo(() => {
    const base = buildProfileFromTalent(talent || {});
    if (dbOverlay) {
      base.summary  = dbOverlay.profSum || base.summary;
      base.snapshot = dbOverlay.snapExp || base.snapshot;

      // Lists from DB
      if (dbOverlay.workExpList?.length) base.workExpList = dbOverlay.workExpList;
      if (dbOverlay.educationList?.length) base.educationList = dbOverlay.educationList;

      if (dbOverlay.techSkills?.length)     base.skills.technical = dbOverlay.techSkills;
      if (dbOverlay.creativeSkills?.length) base.skills.creative  = dbOverlay.creativeSkills;
      if (dbOverlay.collaboration?.length)  base.skills.collab    = dbOverlay.collaboration;

      if (dbOverlay.workPref?.length)       base.preferences      = dbOverlay.workPref;
      if (dbOverlay.certTrainings?.length)  base.certs            = dbOverlay.certTrainings.map((name) => ({ name, year: '' }));
      if (dbOverlay.languages?.length)      base.languages        = dbOverlay.languages;
    }
    return base;
  }, [talent, dbOverlay]);

  const doSave = useCallback(async (overrides = {}) => {
    if (!talentId) return;
    try {
      setSaveBusy(true); setSaveErr("");
      await ProfileAPI.save({
        talentId: String(talentId),
        clientMsg,
        availMap,
        behavioralStatuses,
        skillsStatuses,
        starStatuses,
        secondStatuses,
        ...overrides,
      });
    } catch (e) {
      setSaveErr(e?.message || String(e));
    } finally {
      setSaveBusy(false);
    }
  }, [talentId, clientMsg, availMap, behavioralStatuses, skillsStatuses, starStatuses, secondStatuses]);

  const handleAvail = useCallback(async (testKey) => {
    const next = { ...availMap, [testKey]: true };
    setAvailMap(next);
    await doSave({ availMap: next });
  }, [availMap, doSave]);

  // Simple PDF demo (unchanged)
  const openSimplePdf = useCallback((fileName, title, lines) => {
    const esc = (s) => String(s).replace(/[()\\]/g, (m) => '\\' + m);
    const textOps = [
      'BT /F1 20 Tf 72 740 Td (' + esc(title) + ') Tj ET',
      ...lines.map((ln, i) => 'BT /F1 12 Tf 72 ' + (710 - i*18) + ' Td (' + esc(ln) + ') Tj ET')
    ].join('\n');
    const streamHeader = '<< /Length ' + textOps.length + ' >>\nstream\n';
    const stream = streamHeader + textOps + '\nendstream';
    const parts = [
      '%PDF-1.4\n',
      '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj\n',
      '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj\n',
      '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >> endobj\n',
      '4 0 obj ' + stream + ' endobj\n',
      '5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj\n'
    ];
    let offset = 0;
    const offsets = [];
    const body = parts.map((p) => { offsets.push(offset); offset += p.length; return p; }).join('');
    let xref = 'xref\n0 6\n0000000000 65535 f \n';
    for (let i = 0; i < offsets.length; i++) {
      const pos = String(offsets[i]).padStart(10, '0');
      xref += pos + ' 00000 n \n';
    }
    const trailer = 'trailer << /Size 6 /Root 1 0 R >>\nstartxref\n' + offset + '\n%%EOF';
    const pdf = body + xref + trailer;
    const blob = new Blob([pdf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) { const a = document.createElement('a'); a.href = url; a.download = fileName + '.pdf'; document.body.appendChild(a); a.click(); a.remove(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);

  const viewAssessmentPdf = useCallback((testLabel, status) => {
    const now = new Date().toLocaleString();
    const header = [
      'Candidate : ' + profile.name,
      'Role      : ' + profile.role,
      'Assessment: ' + testLabel,
      'Date      : ' + now,
      ''
    ];
    const body = status === 'Passed'
      ? [
          'Dear Candidate,',
          '',
          'Congratulations!',
          'You have PASSED the "' + testLabel + '" assessment.',
          'This result indicates you currently meet the target benchmark for this evaluation.',
          '',
          '— Exodia Assessment Team'
        ]
      : [
          'Dear Candidate,',
          '',
          'Thank you for taking the assessment.',
          'The result for "' + testLabel + '" is: FAILED.',
          'This is a sample letter. Please review and retake when ready.',
          '',
          '— Exodia Assessment Team'
        ];
    openSimplePdf(
      profile.alias + '-' + testLabel.replace(/\s+/g,'-') + '-Result',
      testLabel + ' — Result',
      header.concat(body)
    );
  }, [openSimplePdf, profile.alias, profile.role, profile.name]);

  // ---------- Add to Shortlist ----------
  const [inShortlist, setInShortlist] = useState(false);
  const [shortlistLoading, setShortlistLoading] = useState(false);

  // Check if talent is in shortlist
  useEffect(() => {
    const checkShortlist = async () => {
      if (!talent?.id || !currentUser?.uid) {
        setInShortlist(false);
        return;
      }
      
      try {
        setShortlistLoading(true);
        const list = await getShortlist(currentUser.uid);
        const myId = String(talent.id);
        const isInList = Array.isArray(list) && list.some(x => String(x.talent_id ?? x.talentId ?? x.id) === myId);
        setInShortlist(isInList);
      } catch (error) {
        console.error('Failed to check shortlist:', error);
        setInShortlist(false);
      } finally {
        setShortlistLoading(false);
      }
    };

    checkShortlist();
  }, [talent?.id, currentUser?.uid]);

  const [addBusy, setAddBusy] = useState(false);

  const handleAddToShortlist = useCallback(async () => {
    if (!talent?.id) { alert('Missing talent ID.'); return; }
    if (!currentUser?.uid) { alert('Please log in to shortlist talents.'); return; }
    if (inShortlist) { alert('Already in your Shortlist.'); return; }
    try {
      setAddBusy(true);
      const payload = {
        talentId: String(talent.id),
        name: profile?.name || raw?.name || '—',
        role: profile?.role || raw?.role || '—',
        alias: profile?.alias || profile?.name || raw?.alias || raw?.name || '—',
        availability: raw?.availability || 'Immediate',
      };
      await addToShortlist(payload, currentUser.uid);
      // Consume shortlist pass for real-time UI update
      await consumeShortlistPass(1);
      setInShortlist(true); // Update local state
      alert('Added to Shortlist!');
      onClose?.(); // optional: close after adding
    } catch (error) {
      console.error('Failed to add to shortlist:', error);
      alert('Failed to add to shortlist. Please try again.');
    } finally {
      setAddBusy(false);
    }
  }, [talent?.id, currentUser?.uid, inShortlist, profile, raw, onClose, consumeShortlistPass]);

  if (!isOpen || !talent) return null;

  const Avatar = () => (
    <div className="tp-avatar-ring">
      {selectedImage ? (
        <img src={selectedImage} alt={`${profile.alias} avatar`} className="tp-avatar-img" />
      ) : (
        <div className="tp-avatar">{profile.alias?.charAt(0) ?? 'U'}</div>
      )}
    </div>
  );

  const renderRow = (label, key, prefix, statusMap, setStatusMap) => {
    const status = statusMap[key];
    const availed = !!(availMap[prefix + ':' + key]);
    const payloadKey =
      prefix === 'behavioral' ? 'behavioralStatuses' :
      prefix === 'skills'     ? 'skillsStatuses' :
      prefix === 'star'       ? 'starStatuses'   :
                                'secondStatuses';
    return (
      <li className="tp-assess-row" key={key}>
        <span className="tp-assess-name">{label}</span>
        {availed ? (
          <div className="tp-assess-actions -inline">
            <div className="tp-mini-toggle" role="radiogroup" aria-label={`${label} result`}>
              <button
                type="button"
                role="radio"
                aria-checked={status === 'Passed'}
                className={`tp-toggle-btn ${status === 'Passed' ? 'active' : ''}`}
                onClick={async () => {
                  const next = { ...statusMap, [key]: 'Passed' };
                  setStatusMap(next);
                  await doSave({ [payloadKey]: next });
                }}
              >✅ Passed</button>
              <button
                type="button"
                role="radio"
                aria-checked={status === 'Failed'}
                className={`tp-toggle-btn ${status === 'Failed' ? 'active' : ''}`}
                onClick={async () => {
                  const next = { ...statusMap, [key]: 'Failed' };
                  setStatusMap(next);
                  await doSave({ [payloadKey]: next });
                }}
              >❌ Failed</button>
            </div>
            <button
              type="button"
              className="tp-view-btn"
              onClick={() => viewAssessmentPdf(label, status)}
              title="Open PDF result in a new tab"
            >
              <span className="btn-ico">📄</span>
              <span className="btn-text">View</span>
              <span className={`status-dot ${status === 'Passed' ? 'ok' : 'bad'}`} />
            </button>
          </div>
        ) : (
          <div className="tp-assess-actions -compact">
            <span className="tp-assess-notice">⏳ Not Availed</span>
            <button
              type="button"
              className="tp-view-btn tp-upgrade"
              onClick={() => handleAvail(prefix + ':' + key)}
              title="Avail this assessment"
            >
              <span className="btn-ico">⚡</span>
              <span className="btn-text">Avail Now</span>
            </button>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="tp-modal-overlay" onClick={onClose}>
      <div
        className="tp-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${profile.alias} profile`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tp-header">
          <Avatar />
          <div className="tp-header-meta">
            <h2 className="tp-title">{profile.alias}</h2>
            <div className="tp-subtitle">
              <span className="tp-badge tp-badge-accent">ID: {profile.candidateId}</span>
              <span className="tp-sep">•</span>
              <span className="tp-role">{profile.role}</span>
              <span className="tp-sep">•</span>
              <span className={`tp-stage tp-stage--${(profile.stage || 'default').toLowerCase().replace(/[^a-z]/g,'')}`}>
                {profile.stage}
              </span>
            </div>
          </div>
          <button className="tp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Topbar */}
        <div className="tp-topbar">
          <div className="tp-topbar-grid">
            <div className="tp-stat">
              <div className="tp-stat-label">
                {loading ? 'Loading…' : (loadErr ? `Error: ${loadErr}` : 'Experience')}
              </div>
              {!loading && !loadErr && <div className="tp-stat-value">{profile.yearsExp}+ yrs</div>}
            </div>
            {saveBusy && <div className="tp-stat"><div className="tp-stat-label">Saving…</div></div>}
            {saveErr && <div className="tp-stat"><div className="tp-stat-label" style={{color:'#f99'}}>Save error: {saveErr}</div></div>}
          </div>
        </div>

        <div className="tp-body">
          {/* ======= Professional Summary ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Professional Summary</h3>
            <p className="tp-paragraph">{profile.summary}</p>
          </section>

          {/* ======= Snapshot of Experience ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Snapshot of Experience</h3>
            <p className="tp-paragraph">{profile.snapshot}</p>
          </section>

          {/* ======= Work Experience ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Work Experience</h3>

            {Array.isArray(profile.workExpList) && profile.workExpList.length > 0 ? (
              <ul className="tp-bullets">
                {profile.workExpList.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : (
              <div className="tp-timeline">
                {profile.experienceTimeline.map((xp, i) => (
                  <div className="tp-timeline-item" key={i}>
                    <div className="tp-timeline-left">
                      <div className="tp-xp-title">{xp.title}</div>
                      <div className="tp-xp-org">{xp.org} | {xp.location}</div>
                      <div className="tp-xp-dates">{xp.dates}</div>
                    </div>
                    <ul className="tp-xp-points">
                      {xp.points.map((p, idx) => (<li key={idx}>{p}</li>))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ======= Education ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Education</h3>

            {Array.isArray(profile.educationList) && profile.educationList.length > 0 ? (
              <div className="tp-list">
                {profile.educationList.map((txt, i) => (
                  <div className="tp-list-item" key={i}>
                    <div className="tp-list-title">{txt}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tp-list">
                {profile.educationTimeline.map((ed, i) => (
                  <div className="tp-list-item" key={i}>
                    <div className="tp-list-title">{ed.degree}</div>
                    <div className="tp-list-sub">{ed.school} | {ed.location}</div>
                    <div className="tp-list-meta">{ed.dates}{ed.note ? ` — ${ed.note}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ======= Core Skills & Capabilities ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Core Skills & Capabilities</h3>
            <div className="tp-skills">
              <div className="tp-skill-group">
                <div className="tp-skill-head">Technical</div>
                <div className="tp-skill-tags">
                  {profile.skills.technical.map((s, i) => <span className="tp-tag" key={i}>{s}</span>)}
                </div>
              </div>
              <div className="tp-skill-group">
                <div className="tp-skill-head">Creative & Functional</div>
                <div className="tp-skill-tags">
                  {profile.skills.creative.map((s, i) => <span className="tp-tag" key={i}>{s}</span>)}
                </div>
              </div>
              <div className="tp-skill-group">
                <div className="tp-skill-head">Collaboration</div>
                <div className="tp-skill-tags">
                  {profile.skills.collab.map((s, i) => <span className="tp-tag" key={i}>{s}</span>)}
                </div>
              </div>
            </div>
          </section>

          {/* ======= Work Preferences ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Work Preferences</h3>
            <ul className="tp-bullets">
              {profile.preferences.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </section>

          {/* ======= Certificates & Trainings ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Certificates & Trainings</h3>
            <div className="tp-list">
              {profile.certs.map((c, i) => (
                <div className="tp-list-item" key={i}>
                  <div className="tp-list-title">{c.name}</div>
                  {c.year ? <div className="tp-list-meta">{c.year}</div> : null}
                </div>
              ))}
            </div>
          </section>

          {/* ======= Languages ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Languages</h3>
            <div className="tp-skill-tags">
              {profile.languages.map((l, i) => <span className="tp-tag" key={i}>{l}</span>)}
            </div>
          </section>

          {/* ======= Client Interview Feedback (DB-backed) ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Client Interview Feedback</h3>
            <p className="tp-paragraph" style={{ marginBottom: 10 }}>
              Add notes or feedback about this talent. Saved to the database and included in the exported PDF.
            </p>
            <textarea
              className="tp-textarea"
              rows={5}
              placeholder="Type client feedback here..."
              value={clientMsg}
              onChange={(e) => setClientMsg(e.target.value)}
              style={{
                width: '100%',
                borderRadius: 10,
                padding: 10,
                border: '1px solid rgba(255,255,255,.18)',
                background: 'rgba(255,255,255,.06)',
                color: '#fff',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8 }}>
              <button className="tp-btn tp-btn-secondary" onClick={() => doSave()}>Save</button>
            </div>
          </section>

          {/* ======= Assessments & Portfolio (DB-backed) ======= */}
          <section className="tp-card">
            <h3 className="tp-card-title">Assessments &amp; Portfolio</h3>

            {/* Behavioral */}
            <div className="tp-assess-block">
              <div className="tp-assess-title">Behavioral &amp; Personality Result</div>
              <div className="tp-divider" />
              <ul className="tp-assess-list -with-actions">
                {BEHAVIORAL_TESTS.map((t) =>
                  renderRow(t.label, t.key, 'behavioral', behavioralStatuses, setBehavioralStatuses)
                )}
              </ul>
            </div>

            {/* Skills */}
            <div className="tp-assess-block">
              <div className="tp-assess-title">Skills &amp; Aptitude Test Result</div>
              <div className="tp-divider" />
              <ul className="tp-assess-list -with-actions">
                {SKILLS_TESTS.map((t) =>
                  renderRow(t.label, t.key, 'skills', skillsStatuses, setSkillsStatuses)
                )}
              </ul>
            </div>

            {/* STAR */}
            <div className="tp-assess-block">
              <div className="tp-assess-title">Initial Interview (STAR Method)</div>
              <div className="tp-divider" />
              <ul className="tp-assess-list -with-actions">
                {STAR_TESTS.map((t) =>
                  renderRow(t.label, t.key, 'star', starStatuses, setStarStatuses)
                )}
              </ul>
            </div>

            {/* CBI */}
            <div className="tp-assess-block">
              <div className="tp-assess-title">Second Interview (CBI)</div>
              <div className="tp-divider" />
              <ul className="tp-assess-list -with-actions">
                {SECOND_INTERVIEW.map((t) =>
                  renderRow(t.label, t.key, 'cbi', secondStatuses, setSecondStatuses)
                )}
              </ul>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="tp-footer">
          <button className="tp-btn tp-btn-secondary" onClick={onClose}>Close</button>
          <button
            className="tp-btn tp-btn-primary"
            onClick={handleAddToShortlist}
            disabled={!talent?.id || addBusy || inShortlist || shortlistLoading}
          >
            {inShortlist ? 'Already in Shortlist' : (addBusy ? 'Adding…' : 'Add to Shortlist')}
          </button>
        </div>
      </div>
    </div>
  );
}
